// src/features/files/upload/FileDropZone.jsx
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

import { useFileDrop } from './useFileDrop';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

/**
 * Makes an area accept files dropped from the desktop. Children render as
 * usual; while files are dragged over the area an overlay says what will
 * happen (`label`, e.g. "Slip filer for at uploade til Udstyr").
 * With `multiple={false}` only the first file is passed on, and the user is told.
 */
export default function FileDropZone({
  onFiles,
  multiple = true,
  disabled = false,
  label,
  children,
  sx,
  ...props
}) {
  const { t } = useTranslation(['files']);
  const { showWarning } = useSnackbar();

  const handleFiles = useCallback((files) => {
    if (!multiple && files.length > 1) {
      showWarning(t('files:upload.only_one', { name: files[0].name }));
      onFiles([files[0]]);
      return;
    }
    onFiles(files);
  }, [multiple, onFiles, showWarning, t]);

  const { dragActive, dropProps } = useFileDrop({ onFiles: handleFiles, disabled });

  return (
    <Box {...props} {...dropProps} sx={{ position: 'relative', ...sx }}>
      {children}
      {dragActive && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            zIndex: theme.zIndex.modal - 1,
            // Let drag events reach the area below, so leaving it is still noticed
            pointerEvents: 'none',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Paper elevation={4} sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: '90%' }}>
            <CloudUploadIcon color="primary" />
            <Typography variant="body1" fontWeight={500}>
              {label || t(multiple ? 'files:upload.drop_files' : 'files:upload.drop_file')}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
